import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Mail, Shield, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Não foi possível processar a solicitação. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Recuperar Senha</h1>
          <p className="text-gray-600">Informe seu e-mail cadastrado para receber o link de redefinição</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl shadow-orange-100/50 p-8">
          {submitted ? (
            <div className="text-center space-y-4" data-testid="forgot-password-success">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full">
                <CheckCircle2 className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Solicitação recebida</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Se o e-mail informado estiver cadastrado, enviaremos um link de redefinição em instantes.
                Confira sua <strong>caixa de entrada</strong> e também a pasta de <strong>spam</strong>.
              </p>
              <p className="text-xs text-gray-500 leading-relaxed">
                O link tem validade de 60 minutos e pode ser utilizado apenas uma vez.
              </p>
              <button
                onClick={() => navigate("/login")}
                data-testid="back-to-login-btn"
                className="w-full h-12 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all font-semibold shadow-lg shadow-orange-500/30"
              >
                Voltar para o Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">E-mail cadastrado</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-12 pl-12 pr-4 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-all outline-none"
                    placeholder="exemplo@viasegsaude.com.br"
                    required
                    autoFocus
                  />
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2 text-sm">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                data-testid="forgot-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all font-semibold shadow-lg shadow-orange-500/30 disabled:opacity-50"
              >
                {loading ? "Enviando..." : "Enviar link de redefinição"}
              </button>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-gray-100">
            <button
              data-testid="back-to-login-link"
              onClick={() => navigate("/login")}
              className="text-orange-600 hover:text-orange-700 text-sm font-medium inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para o Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
